import { getAnalyticsId } from "./gtag";
import { identify } from "./identify";

const wait = (delay: number) => new Promise((resolve) => setTimeout(resolve, delay));

export function getAnonymousId() {
  // Segment generates an anonymousId for us when we call identify without an authToken.
  // We can retrieve it from the analytics object.
  return typeof window?.analytics?.user === "function"
    ? window.analytics?.user()?.anonymousId() || ""
    : "";
}

/**
 * Adds additional authorization parameters to the provided object.
 * @param additionalAuthorizationParams
 */
export async function addAdditionalAuthorizationParams(
  additionalAuthorizationParams: Record<string, any>
): Promise<Record<string, any>> {
  const needsGaClientId = !additionalAuthorizationParams["ext-gaClientId"];
  const needsGaSessionId = !additionalAuthorizationParams["ext-gaSessionId"];
  const needsAnonymousId = !additionalAuthorizationParams["ext-anonymousId"];

  if (!(needsGaClientId || needsGaSessionId || needsAnonymousId)) {
    return additionalAuthorizationParams;
  }

  // If we need to get the segment anonymousId, we need to ensure that the analytics object is initialized.
  // First try and get it.
  let anonymousId = getAnonymousId();
  if (!anonymousId) {
    // If we don't have an anonymousId, we need to ensure that the analytics object is initialized.
    await identify({});
    // Give analytics time to load otherwise we might not have the analytics ID set.
    await wait(300);
    anonymousId = getAnonymousId();
  }

  const [extGaClientId, extGaSessionId] = await getAnalyticsId();

  if (needsGaClientId && extGaClientId) {
    additionalAuthorizationParams["ext-gaClientId"] = extGaClientId;
  }
  if (needsGaSessionId && extGaSessionId) {
    additionalAuthorizationParams["ext-gaSessionId"] = extGaSessionId;
  }
  if (needsAnonymousId && anonymousId) {
    additionalAuthorizationParams["ext-anonymousId"] = anonymousId;
  }

  return additionalAuthorizationParams;
}
